import { useNavigate, Link } from "react-router-dom";
import { Heart, ShoppingCart, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getImageUrl } from "../../utils/imageUtils";
import { storePath } from "../../utils/storeUtils";

export const ProductCard = ({ product, theme, viewMode, onAddToCart, subdomain }) => {
  const navigate = useNavigate();

  const hasCustomizations = product.customizations && (
    (Array.isArray(product.customizations.mandatory) && product.customizations.mandatory.length > 0) ||
    (Array.isArray(product.customizations.optional) && product.customizations.optional.length > 0)
  );

  const handleAction = (e) => {
    e.preventDefault();
    if (hasCustomizations) {
      navigate(storePath(subdomain, `/product/${product.product_id}`));
    } else {
      onAddToCart({
        ...product,
        base_price: product.base_price,
        total_price: product.display_price || product.base_price,
        customizations: []
      });
    }
  };
  if (viewMode === "list") {
    return (
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow flex">
        <div className="w-48 h-48 flex-shrink-0 cursor-pointer">
          <Link to={storePath(subdomain, `/product/${product.product_id}`)}>
            <img
              src={getImageUrl(product.image_url || product.image_urls?.[0]) || "https://images.unsplash.com/photo-1544605562-f72fca6c598d?auto=format&fit=crop&q=80&w=800"}
              alt={product.name}
              className="w-full h-full object-contain bg-slate-50 border-r"
            />
          </Link>
        </div>
        <div className="flex-1 p-4 flex flex-col justify-between">
          <div>
            <Link to={storePath(subdomain, `/product/${product.product_id}`)}>
              <h3
                className="font-semibold text-lg mb-2 hover:text-indigo-600 transition-colors"
                style={{ color: theme.primary_color }}
              >
                {product.name}
              </h3>
            </Link>
            <p className="text-slate-500 text-sm line-clamp-2 mb-2">
              {product.description}
            </p>
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
              ))}
              <span className="text-xs text-slate-500 ml-1">(24)</span>
            </div>
          </div>
          <div className="flex items-center justify-between mt-4">
            <span className="text-2xl font-bold" style={{ color: theme.accent_color }}>
              ${(product.display_price || product.base_price || product.price || 0).toFixed(2)}
            </span>
            <Button
              onClick={handleAction}
              style={{ backgroundColor: theme.primary_color }}
              className="hover:opacity-90"
            >
              {hasCustomizations ? (
                <span className="font-medium text-sm px-2">Select Options</span>
              ) : (
                <>
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Add to Cart
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Grid view (default)
  return (
    <div className="group bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-all duration-300">
      <div className="relative aspect-square overflow-hidden cursor-pointer">
        <Link to={storePath(subdomain, `/product/${product.product_id}`)}>
          <img
            src={getImageUrl(product.image_url || product.image_urls?.[0]) || "https://images.unsplash.com/photo-1544605562-f72fca6c598d?auto=format&fit=crop&q=80&w=800"}
            alt={product.name}
            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
          />
        </Link>
        <div className="absolute top-3 right-3 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <button className="p-2 bg-white rounded-full shadow-md hover:bg-slate-50">
            <Heart className="w-4 h-4 text-slate-600" />
          </button>
        </div>
        {product.is_featured && (
          <div
            className="absolute top-3 left-3 px-2 py-1 text-xs font-medium text-white rounded"
            style={{ backgroundColor: theme.accent_color }}
          >
            Featured
          </div>
        )}
      </div>
      <div className="p-4">
        <Link to={storePath(subdomain, `/product/${product.product_id}`)}>
          <h3
            className="font-semibold mb-1 line-clamp-1 hover:text-indigo-600 transition-colors"
            style={{ color: theme.primary_color }}
          >
            {product.name}
          </h3>
        </Link>
        <p className="text-slate-500 text-sm line-clamp-2 mb-3 min-h-[40px]">
          {product.description}
        </p>
        {/* Star Ratings - Hidden as per request */}
        {/* 
        <div className="flex items-center gap-1 mb-3">
          {[...Array(5)].map((_, i) => (
            <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />
          ))}
          <span className="text-xs text-slate-500 ml-1">(24)</span>
        </div>
        */}
        <div className="flex items-center justify-between">
          <span className="text-xl font-bold" style={{ color: theme.accent_color }}>
            ${(product.display_price || product.base_price || product.price || 0).toFixed(2)}
          </span>
          <Button
            size="sm"
            onClick={handleAction}
            style={{ backgroundColor: theme.primary_color }}
            className="hover:opacity-90"
          >
            {hasCustomizations ? (
              <span className="text-xs px-1">Options</span>
            ) : (
              <ShoppingCart className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
